import { AppBar, Box, Button, Grid, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EmptyList,
	EnhancedSnackbar,
	RouterLinkButton,
} from "@websential/cosmic-v20";
import {
	CLIENT_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { IResponse, IServerError } from "../../models/common";
import { Link, useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatDate, useNavigateToLogout } from "../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedDrawerMenu,
	setSelectedStickyNavBar,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminClientRoutesComponent } from "../RoleBasedLeftMenu/routes/AdminClientRoutes";
import { ClientRoutesComponent } from "../RoleBasedLeftMenu/routes/ClientRoutes";
import { CountryService } from "../../services/common/CountryService";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { IWSIBAssociation } from "../../models/WSIBAssociation";
import { PrimeDataGrid } from "../PrimeDataGrid";
import { WSIBAssociationListDataGridTable } from "./WSIBAssociationListDataGridTable/WSIBAssociationListDataGridTable";
import { WSIBAssociationListHeader } from "./WSIBAssociationListHeader";
import { WSIBAssociationService } from "../../services/WSIBAssociationService";
import { WSIBCategoryService } from "../../services/WSIBCategoryService";
import { WSIBClassService } from "../../services/WSIBClassService";
import { WSIBCodeService } from "../../services/WSIBCodeService";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";

export interface IWSIBAssociationList { }
const WSIB_ASSOCIATION_LIST_QUERY_KEY = "wsib-association-list";

const useWSIBAssociation = (
	runGetListAPICall: boolean,
	provinceId: string,
	classLabel: string,
	codeLabel: string,
	categoryLabel: string,
	effectiveDate: String | null,
	endDate: string | null,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [

			WSIB_ASSOCIATION_LIST_QUERY_KEY,
			runGetListAPICall,
			provinceId,
			classLabel,
			codeLabel,
			categoryLabel,
			effectiveDate,
			endDate,
			params,
			navigateToLogout
		],
		queryFn: (data) => {

			return WSIBAssociationService.getWSIBAssociation(
				runGetListAPICall,
				provinceId,
				classLabel,
				codeLabel,
				categoryLabel,
				effectiveDate,
				endDate,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetCountryDDL = () =>
	useQuery({
		queryKey: ["country-ddl"],
		queryFn: (data) => {
			return CountryService.getCountryDDL();
		},
		refetchOnWindowFocus: false,
	});

const useWSIBClassSetupDDL = (
) =>
	useQuery({
		queryKey: ["wsib-class-ddl"],
		queryFn: (data) => {

			return WSIBClassService.getGetWSIBClassDDL();
		},
		refetchOnWindowFocus: false,
	});

const useWSIBCategorySetupDDL = (
) =>
	useQuery({
		queryKey: ["wsib-category-ddl"],
		queryFn: (data) => {

			return WSIBCategoryService.getGetWSIBCategoryDDL();
		},
		refetchOnWindowFocus: false,
	});

const useWSIBCodeSetupDDL = (
) =>
	useQuery({
		queryKey: ["wsib-code-ddl"],
		queryFn: (data) => {

			return WSIBCodeService.getGetWSIBCodeDDL();
		},
		refetchOnWindowFocus: false,
	});

export const WSIBAssociationList: React.FC<IWSIBAssociationList> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();
	const { AdminClientRoutes } = AdminClientRoutesComponent();
	const { ClientRoutes } = ClientRoutesComponent();

	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoadingProvinceDDL, setIsLoadingProvinceDDL] = useState(false);
	const [selectedRowsValues, setSelectedRowsValues] = useState([]);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "province",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const { id } = useSelector((state: IAppState) => state.accountant);

	const [openDialog, setDialogOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState([]);
	const [statusValue, setStatusValue] = useState("");
	const [apiAction, setApiAction] = useState("");
	const [emptyList, setEmptyList] = useState(false);
	const [searchText, setSearchText] = useState({});
	const [zeroWSIBAssociation, setzeroWSIBAssociation] = useState(true);
	const [wsibAssociationListUpdatedItems, setWSIBAssociationListUpdatedItems] =
		useState<any>(undefined);

	// for delete row
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [wsibAssociationId, setWSIBAssociationId] = useState<any>(0);

	// Bulk Delete
	const [bulkDelete, setBulkDelete] = useState<boolean>(false);
	const [bulkWSIBAssociationId, setBulkWSIBAssociationId] = useState<any>({});
	const [bulkWSIBAssociationDelete, setBulkWSIBAssociationDelete] =
		useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(false);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "province",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);


	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const { isPending: isPendingCountryDDL, data: countryDDL } = useGetCountryDDL();
	const { data: wsibCodeSetupDDL } = useWSIBCodeSetupDDL();
	const { data: wsibCategorySetupDDL } = useWSIBCategorySetupDDL();
	const { data: wsibClassSetupDDL } = useWSIBClassSetupDDL();


	const provinceId = formMethods.getValues("provinceId");
	const classLabel = formMethods.getValues("classLabel");
	const codeLabel = formMethods.getValues("codeLabel");
	const categoryLabel = formMethods.getValues("categoryLabel");
	const effectiveDate = formMethods.getValues("effectiveDate");
	const endDate = formMethods.getValues("endDate");

	const { isPending, data } = useWSIBAssociation(
		runGetListAPICall,
		provinceId,
		classLabel,
		codeLabel,
		categoryLabel,
		formatDate(effectiveDate),
		formatDate(endDate),
		queryParams,
		navigateToLogout
	);
	const wsibAssociationList: any | null = data;


	useEffect(() => {
		if (wsibAssociationList) {
			setWSIBAssociationListUpdatedItems(wsibAssociationList)
		}
	}, [wsibAssociationList])

	// on apply button click api call wsib association list 
	const wsibAssociationListDataAPICall = (
		provinceId: string,
		classLabel: string,
		codeLabel: string,
		categoryLabel: string,
		effectiveDate: String | null,
		endDate: string | null,
	) => {
		const fetchData = async () => {
			setIsLoading(true);

			try {
				const response: any = await WSIBAssociationService.getWSIBAssociation(
					true,
					provinceId,
					classLabel,
					codeLabel,
					categoryLabel,

					formatDate(effectiveDate),
					formatDate(endDate),
					queryParams,
					navigateToLogout
				);

				if (response) {
					setWSIBAssociationListUpdatedItems(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {
		
		wsibAssociationListDataAPICall(
			values.provinceId,
			values.classLabel,
			values.codeLabel,
			values.categoryLabel,
			values.effectiveDate,
			values.endDate,
		);
	};

	const onSearchButtonClicked = () => {
		setQueryParams(searchText);
		setzeroWSIBAssociation(false);
	};

	const onRowEditClicked = useCallback((params: IWSIBAssociation) => {
		let path = `/workplace-safety/association/setup/${params.id}`;
		navigate(path);
	}, []);

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const callAction = () => {
		if (apiAction === "delete") {
			alert(`Delete row with ID: ${selectedIds} has been clicked!`);
			// TODO api call for delete
		} else if (apiAction === "updateStatus") {
			alert(`action need to trigger : ${apiAction}`);
			// TODO api call for update status
		}
		setDialogOpen(false);
	};

	// For Delete Row
	const deleteWSIBAssociationField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				wsibAssociationId: wsibAssociationId,
				navigateToLogout: navigateToLogout,
			};
			deleteWSIBAssociationById(delPayload);
			setDialogOpen(false);
		}
	};

	const onRowDeleteClicked = useCallback((params: IWSIBAssociation) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setApiAction("delete");
		setWSIBAssociationId(params.id);
	}, []);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteWSIBAssociationById } = useMutation({
		mutationFn: WSIBAssociationService.delWSIBAssociation,

		onSuccess: (res: string | IServerError | IResponse | any) => {

			if (typeof (res) === 'string') {
				// remove the cache of the Year fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: res,
					severity: "success",
				});

				return queryClient.invalidateQueries({
					queryKey: [WSIB_ASSOCIATION_LIST_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong.",
					severity: res.status === false ? "error" : "success",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		setStatusValue(event.target.value as string);
		setApiAction("updateStatus");
		setDialogOpen(true);
	};

	// const onKeyFieldClickHandler = (clickedRow: any) => {
	// dispatch(setSelectedWSIBAssociation(clickedRow));
	// dispatch(setSelectedWSIBAssociationName(clickedRow));
	// };

	//bulk delete

	const { isPending: isBulkDeleting, mutate: deleteBulkWSIBAssociationes } =
		useMutation({
			mutationFn: WSIBAssociationService.delBulkWSIBAssociationes,
			onSuccess: (res: any | string | IServerError | IResponse) => {
				if (typeof res === "object") {
					// remove the cache of the Year fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong.",
						severity: res.status === false ? "error" : "success",
					});

					setBulkWSIBAssociationDelete(false);
					return queryClient.invalidateQueries({
						queryKey: [WSIB_ASSOCIATION_LIST_QUERY_KEY],
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				setBulkWSIBAssociationDelete(false);
			},
			onError: (err: any) => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
				setBulkWSIBAssociationDelete(false);
			},
		});

	// For Bulk Delete
	const deleteBulkWSIBAssociationField = () => {
		if (bulkDelete) {
			setBulkDelete(false);
			let payload = {
				wsibAssociationIds: bulkWSIBAssociationId,
				navigateToLogout: navigateToLogout,
			};
			deleteBulkWSIBAssociationes(payload);
			setDialogOpen(false);
		}
	};

	const onRowMultipleDeleteClicked = (params: any) => {

		let bulkDeletePayload = {
			wsibAssociationIds: { wsibAssociationIds: params },
		};
		setDialogOpen(true);
		setBulkDelete(true);
		setApiAction("delete");
		setBulkWSIBAssociationId(bulkDeletePayload);
		setBulkWSIBAssociationDelete(true);
	};

	const selectionToolbar = useCallback((selectedItems: any) => {
		setSelectedIds(selectedItems);

		// if (selectedItems.length) {
		// 	return (
		// 		<Box sx={{ marginTop: theme.spacing(1) }}>
		// 			<Button
		// 				onClick={() => onRowMultipleDeleteClicked(selectedItems)}
		// 				variant="contained"
		// 				color="error"
		// 			>
		// 				Delete
		// 			</Button>
		// 		</Box>
		// 	);
		// }
		return (
			// <Box sx={{ marginTop: theme.spacing(1) }}>
			// 	<RouterLinkButton
			// 		to="/company/wsibAssociation/setup"
			// 		text="Add WSIBAssociation"
			// 	/>
			// </Box>
			<></>
		);
	}, []);

	const upadtedWSIBAssociationListItems = useMemo(() => {
		return (
			wsibAssociationListUpdatedItems &&
			wsibAssociationListUpdatedItems.items &&
			wsibAssociationListUpdatedItems?.items.map((item: any) => ({
				...item,

			}))
		);
	}, [wsibAssociationListUpdatedItems]);

	

	const expandedLabels = ["Workplace Safety", "Association"];
	const expandedWSIBAssociationListOrSetupItem = "List"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Association" &&
				item.label === expandedWSIBAssociationListOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === "Association" && item.label === expandedWSIBAssociationListOrSetupItem && "/workplace-safety/association/list",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: CLIENT_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: false }));


		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_wsibAssociation_list.png";

	return (
		// wsibAssociationList &&
		// 	wsibAssociationList.items &&
		// 	wsibAssociationList.items.length === 0 &&
		// 	zeroWSIBAssociation === true ? (
		// 	<EmptyList
		// 		showImage={true}
		// 		imagePath={image}
		// 		heading={`There's nothing! Add WSIBAssociation to proceed`}
		// 		content={`You can add WSIBAssociation by directly clicking on add WSIBAssociation button.`}
		// 		testId={"wsibAssociation-empty-list"}
		// 		showAddButton={true}
		// 		addButtonLabel={"Add WSIBAssociation"}
		// 		navigateToAdd={"/company/wsibAssociation/setup"}
		// 		showImportButton={false}
		// 		showImportXmlButton={false}
		// 	/>
		// ) : (
		<>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Association List"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<WSIBAssociationListHeader

						countryDDL={countryDDL && countryDDL}
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
						setIsLoadingProvinceDDL={(value: boolean) => setIsLoadingProvinceDDL(value)}
						wsibClassSetupDDL={wsibClassSetupDDL}
						wsibCategorySetupDDL={wsibCategorySetupDDL}
						wsibCodeSetupDDL={wsibCodeSetupDDL}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending || isLoading || isLoadingProvinceDDL || isPendingCountryDDL
					}
					testId="wsib-association-list"
				/>

				<DeleteConfirmationDialog
					open={openDialog}
					deleteActionHandler={
						bulkWSIBAssociationDelete === true
							? deleteBulkWSIBAssociationField
							: deleteWSIBAssociationField
					}
					cancelActionHandler={onCloseHandler}
					testId="wsib-association-list-confirmation-dialog"
				/>
				<WSIBAssociationListDataGridTable
					columns={wsibAssociationListUpdatedItems ? wsibAssociationListUpdatedItems.headerColumns : wsibAssociationList?.headerColumns ?? []}
					rows={
						wsibAssociationListUpdatedItems && upadtedWSIBAssociationListItems
							? upadtedWSIBAssociationListItems
							: wsibAssociationList?.items ?? []
						// wsibAssociationList?.items ?? []
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={true}
					onRowEditClicked={onRowEditClicked}
					onRowDeleteClicked={onRowDeleteClicked}
					isLoading={isPending || isDeleting || isBulkDeleting}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						wsibAssociationListUpdatedItems && wsibAssociationListUpdatedItems.total ? wsibAssociationListUpdatedItems.total : 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					selectionToolbar={selectionToolbar}
					selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
					// searchFieldPlaceholder={"Search by Name..."}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroWSIBAssociation(value)
					}
				// pageTitle="WSIB Association List"
				/>


				{wsibAssociationList ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}

			</Grid>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
